import { useParams } from '@tanstack/react-router';
import WorkbenchLink from '@zeus/core/WorkbenchLink';
import { setPushStatusTag } from '_/constants/common';
import useProjectLockAccess from '_/hooks/useProjectLockAccess';
import { compareSort } from '_/utils/formatUtils';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Table from 'antd/lib/table';
import type { ColumnType } from 'antd/lib/table';
import React, { ReactElement } from 'react';

import classes from './DestinationPushesTable.module.css';

export const RETRY_ALLOWED_STATUSES = ['COMPLETE', 'FAILED', 'INTERNAL_FAILURE', 'REVERTED', 'REVERT_FAILED'];

interface DestinationPush {
  id: string;
  status: string;
  selectedSourceName: string;
  destinationName: string;
  startedAt?: string;
  completedAt?: string;
  snapshotId: string;
  selectedCollectionSlug: string;
  selectedSourceSlug: string;
  metadata?: Record<string, any>;
  error?: string;
}

interface DestinationPushesTableProps {
  cursor?: string;
  destinationPushes: DestinationPush[];
  fetchNextSetOfDestinationPushes?: () => void;
  isLoadingDestinations?: boolean;
  toggleDestinationPushModal?: (type: string, pushId: string) => void;
}

function DestinationPushesTable({
  cursor,
  destinationPushes,
  fetchNextSetOfDestinationPushes,
  isLoadingDestinations = false,
  toggleDestinationPushModal,
}: DestinationPushesTableProps): ReactElement {
  const access = useProjectLockAccess();
  const params = useParams({ strict: false }) as Partial<{
    orgSlug: string;
    projectSlug: string;
  }>;
  const { orgSlug: selectedOrgSlug = '', projectSlug: selectedProjectSlug = '' } = params;
  const scroll = { y: 500, x: 2500 };

  const columnDefs: ColumnType<DestinationPush>[] = [
    {
      title: 'Source',
      dataIndex: 'selectedSourceName',
      render: (text: string) => {
        return text;
      },
      sorter: compareSort('selectedSourceName'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Destination',
      dataIndex: 'destinationName',
      sorter: compareSort('destinationName'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_: string, snapshot: DestinationPush) => setPushStatusTag(snapshot.status),
      sorter: compareSort('status'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Started At',
      dataIndex: 'startedAt',
      sorter: compareSort('startedAt'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Completed At',
      dataIndex: 'completedAt',
      sorter: compareSort('completedAt'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Snapshot',
      dataIndex: 'snapshotId',
      render: (_text: string, push: DestinationPush) => {
        const snapshotId = push.snapshotId;
        return (
          <WorkbenchLink
            href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${push.selectedCollectionSlug}/sources/${push.selectedSourceSlug}/snapshots/${snapshotId}`}
          >
            {snapshotId}
          </WorkbenchLink>
        );
      },
      sorter: compareSort('snapshotId'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Metadata',
      dataIndex: 'metadata',
      width: 1500,
      render: (text: Record<string, any>) => {
        if (text && Object.keys(text).length) {
          return <pre>{JSON.stringify(text, null, 2)}</pre>;
        } else {
          return '{}';
        }
      },
    },
    {
      title: 'Error',
      dataIndex: 'error',
    },
    {
      title: 'Action',
      dataIndex: 'id',
      render: (_: string, push: DestinationPush) => {
        return (
          <Space direction="vertical" size="small">
            {push.status === 'COMPLETE' && (
              <Button
                type="default"
                style={{ marginRight: 10, width: 100 }}
                disabled={!access}
                onClick={() => toggleDestinationPushModal?.('revert', push.id)}
              >
                Revert
              </Button>
            )}
            {RETRY_ALLOWED_STATUSES.includes(push.status) && (
              <Button
                type="primary"
                style={{ marginRight: 10, width: 100 }}
                disabled={!access}
                onClick={() => toggleDestinationPushModal?.('retry', push.id)}
              >
                Re-push
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Table
        virtual
        columns={columnDefs}
        dataSource={destinationPushes}
        rowKey={(record) => record.id}
        pagination={false}
        scroll={scroll}
      />
      {!!cursor && (
        <div className={classes.flexEnd}>
          <Button
            className={classes.buttonStyles}
            onClick={fetchNextSetOfDestinationPushes}
            disabled={isLoadingDestinations}
            loading={isLoadingDestinations}
          >
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}

export default DestinationPushesTable;
